import { HbrOption, HbrSelect, HbrTextarea } from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useUpdateEscalationMutation } from '../../../hooks/escalation-request-hooks';
import { useFetchOpportunity } from '../../../hooks/opportunity-hooks';
import { postItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import {
  ApprovalRequestStatusLevel,
  EscalationLevel,
  EscalationRequest,
  EscalationRequestAssignmentPayload,
} from '../../../types/approval-request';
import { renderErrorMessage, titleCase } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';

type FormValues = {
  escalationType: string | null;
  escalationDetails: string;
};

const initialValues: FormValues = {
  escalationType: null,
  escalationDetails: '',
};

interface BuEscalationModalProps {
  open: boolean;
  onClose: () => void;
  escalation: EscalationRequest;
}

const BuEscalationModal: React.FC<BuEscalationModalProps> = ({
  open,
  onClose,
  escalation
}) => {
  const opportunityId = escalation?.opportunity?.id;
  const { id: userId, name: requester } = useSelector((state: RootState) => state.user.userData);
  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });

  const { control, handleSubmit, reset, formState, watch } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  });
  const selectedEscalationType = watch('escalationType');
  const escalationDetails = watch('escalationDetails');

  const escalationTypes = useMemo(() => {
    if (!escalation) return [];
    const buPath = escalation?.available_support_paths?.find(path => path.assignment_type_level === EscalationLevel.Business);
    if (!buPath) return [];

    return (buPath?.bu_request_mappings ?? [])
      .map(mapping => ({
        id: mapping.mapping_id,
        name: mapping.request_type_name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [escalation]);

  const { mutateAsync: updateEscalation } = useUpdateEscalationMutation(opportunityId);

  const requestTitle = useMemo(
    () => `Escalate to Business Unit - ${escalation?.product?.name ?? 'Unknown Product'} - ${opportunity?.Name ?? 'Unknown Opportunity'}`,
    [escalation, opportunity?.Name]
  );

  const opportunityPhase = useMemo(
    () => (opportunity?.highest_se_phase ? titleCase(opportunity.highest_se_phase) : 'Discovery'),
    [opportunity?.highest_se_phase]
  );

  const buSupportPath = useMemo(
    () =>
      (escalation?.available_support_paths ?? []).find(
        (path) => path.assignment_type_level === EscalationLevel.Business
      ),
    [escalation]
  );

  useEffect(() => {
    if (open) {
      reset(initialValues);
    }
  }, [open, reset]);

  const handleClose = async () => {
    reset(initialValues);
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    if (!buSupportPath || !data.escalationType) return;

    const escalationAssignmentPayload: EscalationRequestAssignmentPayload = {
      escalation_id: escalation.id,
      assignment_type_id: buSupportPath.assignment_type_id,
      status_id: ApprovalRequestStatusLevel.Unassigned,
      assigned_to_id: null,
      product_support_id: buSupportPath.id,
      requested_by_id: userId,
      is_external: true,
      external_system: 'jira',
      mapping_id: Number(data.escalationType)
    };

    const escalationRequestUpdate = {
      id: escalation.id,
      description: escalation.description,
      status_id: ApprovalRequestStatusLevel.ReEscalated
    };

    // Separate these so we have the JIRA ticket created before we update the escalation and trigger refresh
    await postItem('db/escalation-assignment', escalationAssignmentPayload);
    await updateEscalation(escalationRequestUpdate);
    handleClose();
  };

  const submitDisabled =
    !selectedEscalationType ||
    !escalationDetails ||
    escalationDetails.trim() === '' ||
    Object.keys(formState.errors).length > 0;

  return (
    <StbApiModal
      open={open}
      title="Escalate to Business Unit"
      role="save"
      submitLabel="Send Request"
      submitDisabled={submitDisabled}
      submitSuccess="Escalation submitted successfully"
      onClose={handleClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Request Details Panel */}
      <div className="border border-gray-300 rounded-md p-2 mb-4">
        <div className="grid grid-cols-[10rem_1fr] gap-x-4">
          <div className="text-sm font-semibold">Request Title:</div>
          <div className="text-sm text-gray-700 mb-2">{requestTitle}</div>

          <div className="text-sm font-semibold">Requester:</div>
          <div className="text-sm text-gray-700 mb-2">{requester}</div>

          <div className="text-sm font-semibold">Opportunity Phase:</div>
          <div className="text-sm text-gray-700 mb-2">{opportunityPhase}</div>

          <div className="text-sm font-semibold">Forecast Category:</div>
          <div className="text-sm text-gray-700 mb-2">
            {opportunity?.ForecastCategoryName || <span className="italic">No value</span>}
          </div>

          <div className="text-sm font-semibold">Forecast Status:</div>
          <div className="text-sm text-gray-700 mb-2">
            {opportunity?.StageName || <span className="italic">No value</span>}
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <form>
        <Controller
          name="escalationType"
          control={control}
          rules={{
            validate: (value) => (value ? true : 'Escalation type is required'),
          }}
          render={({ field }) => {
            const options = escalationTypes ?? [];
            return (
              <HbrSelect
                value={field.value ?? ''}
                disabled={!escalationTypes || escalationTypes.length === 0}
                onHbr-change={(e) => field.onChange(e.target.value)}
                placeholder="Select an escalation type..."
                error={!!formState.errors['escalationType']}
              >
                <div slot="label">
                  Escalation Type <em className="text-gray-500">(required)</em>
                </div>
                {options.map((option) => (
                  <HbrOption key={option.id} value={String(option.id)}>
                    {option.name}
                  </HbrOption>
                ))}
                {renderErrorMessage(
                  !!formState.errors['escalationType'],
                  formState.errors['escalationType']?.message as string
                )}
              </HbrSelect>
            );
          }}
        />

        <Controller
          name="escalationDetails"
          control={control}
          rules={{
            validate: (value) =>
              value && value.trim() !== '' ? true : 'Escalation details are required',
          }}
          render={({ field }) => (
            <HbrTextarea
              value={field.value}
              onHbr-input={(e) => field.onChange(e.target.value)}
              rows={4}
              error={!!formState.errors['escalationDetails']}
            >
              <div slot="label">
                Escalation Details <em className="text-gray-500">(required)</em>
              </div>
              {renderErrorMessage(
                !!formState.errors['escalationDetails'],
                formState.errors['escalationDetails']?.message as string
              )}
            </HbrTextarea>
          )}
        />
      </form>
    </StbApiModal>
  );
};

export default BuEscalationModal;
